import { useMemo, useState } from "react";
import { t } from "../locale";
import type { OfferingSlot } from "../../lib/sessionsApi";
import { formatDateOnly, dateKeyOf } from "../lib/offering-date";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";

/**
 * SlotPicker — the day/slot selection portion extracted from
 * slottingBooking.tsx (W3). Deliberately NOT the whole booking widget:
 * an OFFERING program block pins exactly ONE treatment (strict 1:1), so
 * the offering/treatment selection steps of SlottingBooking are redundant
 * here. Props-driven and stateless about the selection — the owning card
 * (sessionGroup's OFFERING card) holds the picked slot; only the currently
 * browsed day is local UI state.
 *
 * Rendering idioms are lifted from SlottingBooking (date-chip row +
 * auto-fill slot grid, `spotsLeft <= 0` slots never offered), with colors
 * expressed through the wb CSS variables sessionGroup uses (identical
 * fallback hexes, so the look matches SlottingBooking on unthemed sites).
 */

export type SlotSelection = {
  laneId: string;
  /** ISO instants — passed through verbatim to the submit payload. */
  startDt: string;
  endDt: string;
};

type SlotPickerProps = {
  slots: OfferingSlot[];
  selected: SlotSelection | null;
  /** null = deselect (clicking the selected slot toggles it off). */
  onSelect: (slot: SlotSelection | null) => void;
  /** Slot length of the pinned treatment (informational). */
  durationMinutes?: number;
  disabled?: boolean;
  showAvailableCount?: boolean;
};

// `dateKeyOf` lives in runtime/lib/offering-date (audit 2026-09-23, 4d) so
// every slotting surface buckets days the same way.

export function SlotPicker({
  slots,
  selected,
  onSelect,
  durationMinutes,
  disabled,
  showAvailableCount,
}: SlotPickerProps) {
  const eventTz = useEventTimezone();

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const slot of slots) {
      // Exhausted slots (spotsLeft 0) are emitted for calendar "Full"
      // states — never offer them in the picker (SlottingBooking rule).
      if (slot.spotsLeft <= 0) continue;
      dates.add(dateKeyOf(slot.startDt, eventTz));
    }
    return Array.from(dates).sort();
  }, [slots, eventTz]);

  // Browsed day: the selection's day when present, else the only day when
  // there is exactly one (fewer clicks in the embedded card), else none.
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const selectedDate =
    pickedDate ??
    (selected ? dateKeyOf(selected.startDt, eventTz) : null) ??
    (availableDates.length === 1 ? (availableDates[0] ?? null) : null);

  const filteredSlots = useMemo(() => {
    if (!selectedDate) return [];
    return slots.filter(
      (s) => s.spotsLeft > 0 && dateKeyOf(s.startDt, eventTz) === selectedDate,
    );
  }, [slots, selectedDate, eventTz]);

  const mutedColor = "var(--wb-primary, #64748b)";
  const secondaryColor = "var(--wb-secondary, #2563eb)";
  const defaultBorder =
    "1px solid color-mix(in srgb, var(--wb-secondary, #e2e8f0) 30%, transparent)";
  const selectedBorder = `2px solid ${secondaryColor}`;
  const selectedBg =
    "color-mix(in srgb, var(--wb-secondary, #2563eb) 5%, transparent)";

  const timeOf = (iso: string): string =>
    new Date(iso).toLocaleTimeString(undefined, {
      timeZone: eventTz,
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div
      data-testid="slot-picker"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : undefined,
      }}
    >
      {/* Date picker (SlottingBooking chip row) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {availableDates.length === 0 ? (
          <p style={{ color: mutedColor, fontSize: "0.8125rem", margin: 0 }}>
            {t("slottingNoAvailableDates")}
          </p>
        ) : (
          availableDates.map((date) => (
            <button
              key={date}
              type="button"
              data-testid={`slot-date-${date}`}
              onClick={() => setPickedDate(date)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: "0.8125rem",
                cursor: "pointer",
                border: selectedDate === date ? selectedBorder : defaultBorder,
                background: selectedDate === date ? selectedBg : "transparent",
              }}
            >
              {formatDateOnly(date, undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </button>
          ))
        )}
      </div>

      {/* Time slots (SlottingBooking auto-fill grid) */}
      {selectedDate ? (
        filteredSlots.length === 0 ? (
          <p style={{ color: mutedColor, fontSize: "0.8125rem", margin: 0 }}>
            {t("slottingNoAvailableTimes")}
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 8,
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            }}
          >
            {filteredSlots.map((slot, i) => {
              const isSelected =
                selected !== null &&
                selected.startDt === slot.startDt &&
                selected.laneId === slot.laneId;
              return (
                <button
                  key={`${slot.laneId}-${slot.startDt}-${i}`}
                  type="button"
                  data-testid={`slot-option-${slot.laneId}-${slot.startDt}`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    onSelect(
                      isSelected
                        ? null
                        : {
                            laneId: slot.laneId,
                            startDt: slot.startDt,
                            endDt: slot.endDt,
                          },
                    )
                  }
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    cursor: "pointer",
                    border: isSelected ? selectedBorder : defaultBorder,
                    background: isSelected ? selectedBg : "transparent",
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {timeOf(slot.startDt)} &ndash; {timeOf(slot.endDt)}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: mutedColor,
                      marginTop: 2,
                    }}
                  >
                    {slot.laneName}
                    {showAvailableCount !== false
                      ? ` · ${t("sessionSeatsLeft", String(slot.spotsLeft))}`
                      : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : null}

      {typeof durationMinutes === "number" && durationMinutes > 0 ? (
        <span style={{ fontSize: "0.75rem", color: mutedColor, opacity: 0.8 }}>
          {durationMinutes} min
        </span>
      ) : null}
    </div>
  );
}
